<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="modelValue" class="modal-overlay" @click="onOverlayClick">
        <div
          class="modal"
          :class="[`modal--${size}`, modalClass]"
          role="dialog"
          aria-modal="true"
          @click.stop
        >
          <header class="modal__header">
            <div class="modal__header-text">
              <h2 class="modal__title">
                <Icon
                  v-if="icon"
                  :name="icon"
                  :size="20"
                  :class="['modal__title-icon', iconVariant && `modal__title-icon--${iconVariant}`]"
                />
                <slot name="title">{{ title }}</slot>
              </h2>
              <p v-if="subtitle || $slots.subtitle" class="modal__subtitle">
                <slot name="subtitle">{{ subtitle }}</slot>
              </p>
            </div>
            <button
              v-if="closable"
              class="modal__close"
              type="button"
              :aria-label="$t ? $t('common.close') : 'Close'"
              @click="close"
            >
              <Icon name="X" :size="18" />
            </button>
          </header>

          <div class="modal__body">
            <slot />
          </div>

          <footer v-if="$slots.footer" class="modal__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import Icon from './Icon.vue';

/**
 * BaseModal — controlled via v-model (boolean).
 *
 * Props:
 *   title, subtitle, icon (Lucide), size (sm|md|lg|xl), closable, closeOnOverlay
 */
export default {
  name: 'BaseModal',
  components: { Icon },
  inheritAttrs: false,
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    icon: { type: String, default: '' },
    iconVariant: { type: String, default: '' },
    size: { type: String, default: 'md' }, // sm, md, lg, xl
    modalClass: { type: String, default: '' },
    closable: { type: Boolean, default: true },
    closeOnOverlay: { type: Boolean, default: true },
  },
  emits: ['update:modelValue', 'close', 'confirm', 'cancel'],
  watch: {
    modelValue(open) {
      if (open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
  mounted() {
    this._esc = (e) => {
      if (e.key === 'Escape' && this.modelValue && this.closable) this.close();
    };
    document.addEventListener('keydown', this._esc);
  },
  unmounted() {
    document.removeEventListener('keydown', this._esc);
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    onOverlayClick() {
      if (this.closeOnOverlay && this.closable) this.close();
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: var(--overlay-modal);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
}

.modal {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-strong);
}

.modal--sm { max-width: 420px; }
.modal--md { max-width: 600px; }
.modal--lg { max-width: 820px; }
.modal--xl { max-width: 1040px; }

.modal__header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid var(--border-light);
  background: var(--surface);
  flex-shrink: 0;
}

.modal__header-text { flex: 1 1 auto; min-width: 0; }

.modal__title {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-family: var(--font-family-serif);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--primary);
  line-height: 1.3;
}

.modal__title-icon { color: var(--secondary); }
.modal__title-icon--danger { color: var(--accent); }
.modal__title-icon--warning { color: var(--warning); }
.modal__title-icon--success { color: var(--success); }
.modal__title-icon--info { color: var(--info); }

.modal__subtitle {
  margin: var(--space-1) 0 0 0;
  font-size: var(--font-size-sm);
  color: var(--medium-gray);
}

.modal__close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius);
  color: var(--medium-gray);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}
.modal__close:hover { background-color: var(--light-gray); color: var(--dark); }

.modal__body {
  padding: var(--space-6);
  overflow-y: auto;
  flex: 1 1 auto;
}

.modal__footer {
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--border-light);
  background: var(--surface);
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  flex-wrap: wrap;
  flex-shrink: 0;
}

/* Transition */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity var(--transition-base);
}
.modal-fade-enter-active .modal, .modal-fade-leave-active .modal {
  transition: transform var(--transition-base), opacity var(--transition-base);
}
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .modal, .modal-fade-leave-to .modal {
  transform: translateY(-12px) scale(0.98);
  opacity: 0;
}

@media (max-width: 768px) {
  .modal--lg, .modal--xl { max-width: 100%; }
  .modal__header, .modal__body, .modal__footer { padding-left: var(--space-4); padding-right: var(--space-4); }
}

@media (prefers-reduced-motion: reduce) {
  .modal-fade-enter-active,
  .modal-fade-leave-active,
  .modal-fade-enter-active .modal,
  .modal-fade-leave-active .modal {
    transition: none;
  }
}
</style>
